import { toast } from 'react-toastify';
import { dealerProvider } from '../../services/api';
import { useState } from 'react';
import { sendNotification } from '../../utils/functions';
import { User } from '../../interfaces/users';

type ListUserQueryParams = Partial<{
  type: string;
  term: string;
  currentPage: number;
  pageSize: number;
  tipoPerfil: string;
}> & {
  isNew: boolean;
};

export const useDealer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [isLoadingAPICall, setIsLoadingAPICall] = useState(false);

  const [dealers, setDealers] = useState<any>([]);
  const [metadata, setMetadata] = useState<any>([]);
  const [error, setError] = useState<any>(null);

  const handleAPIError = (e: any) => {
    setIsLoading(false);
    setError(
      e.response.data.errors ||
        e.response ||
        e.message ||
        'Erro desconhecido na API',
    );
  };

  const registerDealer = async () => {
    const companyData = JSON.parse(
      sessionStorage.getItem('companyData') || '{}',
    );
    const locationData = JSON.parse(
      sessionStorage.getItem('locationData') || '{}',
    );
    const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');

    const dealerData = {
      nome: userData.nome || '',
      sobrenome: userData.sobrenome || '',
      email: userData.email || '',
      cnpj: companyData.cnpj || '',
      senha: '',
      inscricaoEstadual: companyData.inscricao || '',
      razaoSocial: companyData.corporateName || '',
      nomeFantasia: companyData.nomeFantasia || '',
      endereco:
        `${locationData.endereco}, ${locationData.numero} - ${locationData.complemento}` ||
        '',
      bairro: locationData.bairro || '',
      uf: locationData.uf || '',
      municipio: locationData.municipio || '',
      cep: locationData.cep || '',
      telefone: userData.telefone || '',
    };

    try {
      const response = await dealerProvider.post('/api/cadastrar', dealerData);

      if (response.status === 200 || response.status === 201) {
        return response.data;
      }
    } catch (error) {
      toast.error('Erro ao cadastrar o revendedor');
    }
  };

  const fetchDealerLogs = async ({
    search = null,
    currentPage,
    pageSize,
  }: {
    search?: string | null;
    currentPage: number;
    pageSize: number;
  }) => {
    try {
      setIsLoading(true);
      const searchParam = search ? `&search=${search}` : '';
      const response = await dealerProvider.get(
        `/api/revendedor-allied/logs-integracao-erp-jundsoft?page=${currentPage}&pageSize=${pageSize}${searchParam}`,
      );

      if (response.status === 200) {
        return response.data;
      } else {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      toast.error('Erro ao buscar logs do revendedor');
    } finally {
      setIsLoading(false);
    }
  };

  const exportDealerLogs = async (id: number) => {
    try {
      setIsLoadingExport(true);
      const response = await dealerProvider.get(
        `/api/revendedor-allied/exportar-logs-integracao-erp-jundsoft?Id=${id}`,
        {
          responseType: 'arraybuffer',
        },
      );

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'text/plain' });

        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `revendedor-${id}.txt`;
        document.body.appendChild(a);

        a.click();

        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      toast.error('Erro ao exportar logs do revendedor');
    } finally {
      setIsLoadingExport(false);
    }
  };

  const fetchTermsOfUse = async () => {
    try {
      setIsLoading(true);
      const response = await dealerProvider.get('/api/obter-termo-uso');

      if (response.status === 200) {
        return { data: response.data, error: null };
      } else {
        const errorMsg = `Erro ${response.status}: ${response.statusText}`;
        return { data: null, error: errorMsg };
      }
    } catch (error: any) {
      toast.error('Erro ao buscar os termos de uso');
      return { data: null, error: error?.message || 'Erro inesperado' };
    } finally {
      setIsLoading(false);
    }
  };

  const returnAllDealers = async ({
    type,
    term,
    currentPage,
    pageSize,
    tipoPerfil,
    isNew,
  }: ListUserQueryParams) => {
    const params = {
      search: type,
      text: term,
      page: currentPage?.toString(),
      pagesize: pageSize?.toString(),
      tipoPerfil,
      isNew: isNew.toString(),
    };

    const queryString = new URLSearchParams(
      Object.entries(params).reduce((i, [key, value]) => {
        if (value !== undefined && value !== '') {
          i[key] = value;
        }
        return i;
      }, {} as Record<string, string>),
    ).toString();

    const url = `api/listar/revendedores${
      queryString ? `?${queryString}` : ''
    }`;

    try {
      setIsLoading(true);
      const { data } = await dealerProvider.get(url);
      setMetadata(data.data);
      setDealers(data.data);
    } catch (e: unknown) {
      handleAPIError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const changeUserStatus = async (id: string, activate: boolean) => {
    await performAPIRequest('api/trocastatus', { id, ativo: activate });
  };

  const performAPIRequest = async (url: string, data: any) => {
    try {
      setIsLoadingAPICall(true);
      const response = await dealerProvider.post(url, data);

      setIsLoadingAPICall(false);

      return response;
    } catch (e: any) {
      setIsLoadingAPICall(false);
      handleAPIError(e);
      sendNotification(`Error - ${e.response.data.errors || 'desconhecido'}`, {
        type: 'error',
      });
    }
  };

  const editUserInfo = async (user: any) => {
    const { id, areasAtuacao, grupoEconomico, perfil, cnpjsRaiz } = user || {};

    const requestData = {
      id,
      areasAtuacao,
      grupoEconomico,
      cnpjsRaiz,
      perfil: {
        id: perfil.id,
        nome: perfil.nome,
      },
    };

    return performAPIRequest('api/editar', requestData);
  };

  const analyzeUser = async (
    id: string,
    analyzed: boolean,
    text?: string,
    isNew?: boolean,
    user?: any,
  ) => {
    const requestData: any = {
      id,
      analisado: analyzed,
      justificativa: text,
      gruposEconomicos: user?.gruposEconomicos,
    };

    try {
      setIsLoadingAPICall(true);
      try {
        const response = await dealerProvider.post('api/analise', requestData);
        await returnAllDealers({
          currentPage: 1,
          pageSize: 20,
          isNew: isNew ?? false,
        });
        return response;
      } catch (e: any) {
        handleAPIError(e);
      } finally {
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoadingAPICall(false);
      handleAPIError(error);
      sendNotification(
        `Error - ${error.response.data.errors || 'desconhecido'}`,
        {
          type: 'error',
        },
      );
    } finally {
      setIsLoadingAPICall(false);
    }
  };

  const editUser = async (user: User) => {
    setIsLoadingAPICall(true);
    try {
      const response = await dealerProvider.put<User>(
        'api/revendedor-allied',
        user,
      );

      return response?.data;
    } catch (e: any) {
      return e?.response;
    } finally {
      setIsLoadingAPICall(false);
    }
  };

  return {
    registerDealer,
    fetchDealerLogs,
    exportDealerLogs,
    fetchTermsOfUse,
    returnAllDealers,
    changeUserStatus,
    editUser,
    editUserInfo,
    analyzeUser,
    dealers,
    isLoading,
    isLoadingExport,
    isLoadingAPICall,
    metadata,
  };
};

export default useDealer;
